/**
 * Starter prompts from the open document — `POST /document-context/starter-prompts`
 * on the local agent (item BH5). Ported from `bimgo-ai-web`'s
 * `starterPromptService.ts`: the same contract version, the same body, the
 * same response shape. What differs is the year: 2.5 names the bound instance
 * (`target_session_id`) and the agent reads that instance's context file.
 */
import type { JSONContent } from "@tiptap/core";
import { agentClient, type AgentClient } from "./agentClient";
import { parseAskError } from "./askError";
import type { DocumentContextItem } from "./documentContext";
import { bearer } from "./accessToken";
import { requireDocumentContextElementId } from "./elementId";
import type { CurrentModel, ReasoningEffort } from "./models";

export const STARTER_PROMPT_CONTRACT_VERSION = 1;

export interface StarterPromptMention {
  type: string;
  id: string;
  label: string;
  familyName?: string;
  typeName?: string;
  derivedFromTypeId?: string;
  derivedFromTypeName?: string;
}

export interface StarterPrompt {
  id: string;
  intent: "understand" | "inspect" | "export";
  beforeText: string;
  mention: StarterPromptMention;
  afterText: string;
}

export interface StarterPromptResponse {
  contractVersion: number;
  source: "ai" | "context_fallback";
  snapshotToken: string;
  document: { name: string; lastUpdated: string | null };
  prompts: StarterPrompt[];
  fallbackReason?: "usage_limit" | "model_unavailable" | "generation_error";
}

export class StarterPromptError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "StarterPromptError";
    this.status = status;
  }
}

export async function fetchStarterPrompts(
  params: {
    token: string;
    organizationId: string;
    projectId: string;
    locale: "zh" | "en";
    targetSessionId: string;
    /**
     * The composer's selected model (#127). Null before the catalogue has loaded, and
     * then the agent's configured fallback runs — the same shape `/ask` uses, and the
     * agent plan-checks whatever it is sent, so a model the org cannot use is refused
     * here exactly as it is for a turn.
     */
    model?: CurrentModel | null;
    /**
     * The composer's reasoning chip. Sent only when the chip applies to the selected
     * model, the way a turn sends it — a model that does not reason gets no effort, so
     * the agent's own handling applies unchanged.
     */
    reasoningEffort?: ReasoningEffort;
    signal?: AbortSignal;
  },
  client: AgentClient = agentClient,
): Promise<StarterPromptResponse> {
  const response = await client.fetch("/document-context/starter-prompts", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: bearer(params.token),
      "X-Organization-Id": params.organizationId,
    },
    body: JSON.stringify({
      contractVersion: STARTER_PROMPT_CONTRACT_VERSION,
      locale: params.locale,
      organization_id: params.organizationId,
      project_id: params.projectId,
      target_session_id: params.targetSessionId,
      // Both or neither: the agent cannot plan-check half a pair, and sending one
      // alone would silently fall back to its configured default.
      ...(params.model
        ? { llm_provider: params.model.provider, llm_model: params.model.model }
        : {}),
      ...(params.reasoningEffort ? { reasoning_effort: params.reasoningEffort } : {}),
    }),
    ...(params.signal ? { signal: params.signal } : {}),
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsed = parseAskError(response.status, payload, response.statusText);
    throw new StarterPromptError(response.status, parsed.message || `HTTP ${response.status}`);
  }
  const data = payload as StarterPromptResponse | null;
  if (!data || data.contractVersion !== STARTER_PROMPT_CONTRACT_VERSION || !Array.isArray(data.prompts)) {
    throw new StarterPromptError(response.status, "Unexpected starter prompt response");
  }
  return data;
}

/**
 * The item a prompt names, found again in the live list — by id, then by name
 * (the old `findFreshStarterPromptItem`). Prompts are generated from a
 * snapshot; the element may have been deleted or re-created since, and a
 * chip must never carry a dead id into the model's tool calls.
 */
export function findFreshStarterPromptItem(prompt: StarterPrompt, items: readonly DocumentContextItem[]): DocumentContextItem | null {
  const wanted = requireDocumentContextElementId(prompt.mention.type, prompt.mention.id);
  const byId = items.find(
    (item) =>
      requireDocumentContextElementId(prompt.mention.type, item.id) === wanted,
  );
  if (byId) return byId;
  const label = prompt.mention.label.trim().toLowerCase();
  if (!label) return null;
  return items.find((item) => typeof item.name === "string" && item.name.trim().toLowerCase() === label) ?? null;
}

/**
 * The prompt as editor content — text, a mention node, text — the old
 * `starterPromptToEditorContent`. The node's family fields prefer the live
 * item's over the prompt's, for the same freshness reason.
 */
export function starterPromptToEditorContent(prompt: StarterPrompt, item: DocumentContextItem): JSONContent {
  const content: JSONContent[] = [];
  if (prompt.beforeText) content.push({ type: "text", text: prompt.beforeText });
  content.push({
    type: "mention",
    attrs: {
      type: prompt.mention.type,
      id: requireDocumentContextElementId(prompt.mention.type, item.id),
      label: item.name,
      familyName: item.familyName ?? prompt.mention.familyName ?? null,
      typeName: item.typeName ?? prompt.mention.typeName ?? null,
      derivedFromTypeId: item.derivedFromTypeId ?? prompt.mention.derivedFromTypeId ?? null,
      derivedFromTypeName: item.derivedFromTypeName ?? prompt.mention.derivedFromTypeName ?? null,
      selectedParams: [],
    },
  });
  if (prompt.afterText) content.push({ type: "text", text: prompt.afterText });
  return { type: "doc", content: [{ type: "paragraph", content }] };
}

/**
 * The prompt as plain text — the fallback when its mention cannot be found
 * in the open document (the type is gone, or no instance is bound): the
 * words still help, as `@label`, the way the chip reads once flattened.
 */
export function renderStarterPrompt(prompt: StarterPrompt): string {
  return `${prompt.beforeText}@${prompt.mention.label}${prompt.afterText}`.replace(/\s+/g, " ").trim();
}

/** Which notice a response earns — the old panel's `noticeKey` rule. */
export function starterNoticeKey(response: StarterPromptResponse): string | null {
  if (response.source === "ai") return null;
  return response.fallbackReason === "usage_limit" ? "starterPrompts.usageFallback" : "starterPrompts.contextFallback";
}
